import React, {
  Children,
  cloneElement,
  isValidElement,
  useMemo
} from "react";

import { createPortal } from "react-dom";

import {
  WindowContext,
  useOwningWindow,
  useWindowManager
} from "./WindowManager";

import { resolveWindowLayer } from "./WindowLayer";

const classNames = (...values) =>
  values.filter(Boolean).join(" ");

export function Window({
  layer,
  aboveOwner = false,
  offset = 1,
  portal = false,
  asChild = false,
  as: Element = "div",
  className,
  style,
  children,
  ...rest
}) {
  const manager = useWindowManager();
  const owner = useOwningWindow();

  if (layer !== undefined && aboveOwner) {
    throw new Error(
      "Window cannot specify both layer and aboveOwner"
    );
  }

  const zIndex =
    layer !== undefined && layer !== null
      ? resolveWindowLayer(layer)
      : aboveOwner
        ? owner.zIndex + offset
        : owner.zIndex;

  const windowContext = useMemo(
    () => ({ zIndex }),
    [zIndex]
  );

  let surface;

  if (asChild) {
    const child = Children.only(children);

    if (!isValidElement(child)) {
      throw new Error(
        "Window with asChild requires one React element"
      );
    }

    surface = cloneElement(child, {
      ...rest,
      className: classNames(
        child.props.className,
        className
      ),
      style: {
        ...child.props.style,
        ...style,
        zIndex
      },
      "data-window-z": zIndex
    });
  } else {
    surface = (
      <Element
        {...rest}
        className={className}
        style={{
          position: "relative",
          ...style,
          zIndex
        }}
        data-window-z={zIndex}
      >
        {children}
      </Element>
    );
  }

  const ownedSurface = (
    <WindowContext.Provider value={windowContext}>
      {surface}
    </WindowContext.Provider>
  );

  if (!portal) {
    return ownedSurface;
  }

  const target = manager?.portalTarget;

  return target
    ? createPortal(ownedSurface, target)
    : ownedSurface;
}
